import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Tooltip from '../../../../components/react/tooltip/tooltip';
import { AlertHandler, SuccessAlert } from '../../../../components/react/alert_handler/alert_handler';
import { removeContactFromOrganization } from '../../../../services/organizations/organizations.service';
import { newMessagePath } from '../../../../utilities/api_relative_paths';
import { showErrors } from '../../../../utilities/fetch';
import { createQueryString } from '../../../../utilities/utilities';
import styles from './organization_contact.module.scss';

const alertHandler = new AlertHandler();

const OrganizationContact = (props) => {
  const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false);
  const [deleting, setIsDeleting] = useState(false);
  const {
    email,
    phone,
    id: contactId,
    consultant,
    canEdit,
    contactDeleted,
    organizationId,
    type,
    modifyContact,
  } = props;

  let { name, job_title: jobTitle } = props;

  if (consultant) {
    if (consultant.full_name) name = consultant.full_name;
    if (consultant.headline) jobTitle = consultant.headline;
  }

  const contactHref =
    consultant &&
    consultant.id &&
    `${newMessagePath}${createQueryString({
      recipient_ids: [consultant.id],
      return_to: window.location.pathname,
    })}`;

  const handleDeleteButton = () => {
    if (deleting) return;
    setIsDeleting(true);
    removeContactFromOrganization(organizationId, contactId)
      .then(() => {
        const successMessage = `${name} was successfully removed from ${type} staff`;
        alertHandler.render(SuccessAlert, successMessage);
        setIsDeleteContactModalOpen(false);
        contactDeleted();
      })
      .catch((error) => {
        showErrors(error, alertHandler);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const deleteContactModal = (
    <Modal
      data-testid='delete-contact-modal'
      show={isDeleteContactModalOpen}
      onHide={() => setIsDeleteContactModalOpen(false)}
    >
      <Modal.Header closeButton={true}>
        <h2 className='modal-title fs-3'>
          Delete Staff
        </h2>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex justify-content-center flex-column align-items-center'>
          <i className={`fal icon-user-slash text-danger ${styles.iconModal}`}></i>
          <p className={`fs-4 ${styles.deleteModalMainText}`}>
            Are you sure you want to remove {name}?
          </p>
          <p className='text-gray-li fs-7 text-center'>
            Their information will be removed from the organization profile. There will be no way to
            undo this.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={() => {
            setIsDeleteContactModalOpen(false);
          }}
          className='btn btn-outline-info'
        >
          Cancel
        </button>
        <button onClick={handleDeleteButton} className='btn btn-outline-primary'>
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </Modal.Footer>
    </Modal>
  );

  const displayDeleteOption = canEdit && (
    <>
      <a data-testid='delete-option' onClick={() => setIsDeleteContactModalOpen(true)}>
        <Tooltip message='Delete' placement='top'>
          <i className='fal icon-trash text-blue cursor-pointer'></i>
        </Tooltip>
        <span className='visually-hidden'>Delete</span>
      </a>
      {deleteContactModal}
    </>
  );

  const displayEditOption = canEdit && (
    <>
      <span className='text-blue mx-15'>|</span>
      <div data-testid='edit-option'>
        <a onClick={modifyContact}>
          <Tooltip message='Edit' placement='top'>
            <i className='fal icon-pencil text-blue cursor-pointer'></i>
          </Tooltip>
          <span className='visually-hidden'>Edit</span>
        </a>
      </div>
    </>
  );

  const nameDisplay = contactHref ? (
    <a className='strong' data-testid='name-link' href={`/people/${consultant.id}`}>
      <span data-testid='name'>{name}</span>
    </a>
  ) : (
    <p data-testid='name'>{name}</p>
  );

  const phoneDisplay = phone && (
    <p className='small text-gray-li'>
      <i className='fal icon-phone-alt me-15'></i>
      {phone}
    </p>
  );

  const plausibleContactClass = `
    plausible-event-name=click
    plausible-event-intention=contact
    plausible-event-section=${type}-staff`;

  const contactDisplay = contactHref && (
    <a
      className={`btn btn-sm btn-outline-info ${plausibleContactClass}`}
      data-testid='contact-option'
      href={contactHref}
    >
      <i className='fal icon-envelope me-15'></i>Contact
    </a>
  );

  const mailToDisplay = email && (
    <a
      className={`btn btn-sm btn-outline-info ${plausibleContactClass}`}
      data-testid='mail-to-option'
      href={`mailto: ${email}`}
    >
      <i className='fal icon-envelope me-15'></i>Contact
    </a>
  );

  return (
    <div className={styles.contact} data-testid='organization-contact-component'>
      <div className='mb-25'>
        {nameDisplay}
        {jobTitle && (
          <p className='small text-gray-li' data-testid='title'>
            {jobTitle}
          </p>
        )}
        {phoneDisplay}
      </div>
      {consultant ? contactDisplay : mailToDisplay}
      <div className={styles.pullRight}>
        {displayDeleteOption}
        {displayEditOption}
      </div>
    </div>
  );
};

OrganizationContact.propTypes = {
  id: PropTypes.number.isRequired,
  consultant: PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    headline: PropTypes.string,
  }),
  email: PropTypes.string,
  phone: PropTypes.string,
  name: PropTypes.string,
  job_title: PropTypes.string,
  canEdit: PropTypes.bool,
  organizationId: PropTypes.string,
  modifyContact: PropTypes.func,
  contactDeleted: PropTypes.func,
  type: PropTypes.string,
};

export default OrganizationContact;
